import React, { useEffect, useState } from "react";
import axios from "axios";
import { Select, MenuItem } from "@mui/material";
import { ChevronDown } from "react-feather";
import { toast } from "react-smart-toaster";
import RequestsByTypeChart from "./RequestsByTypeChart";
import AvgCompletionChart from "./AvgCompletionChart";
import CompletedRequestsChart from "./CompletedRequestsChart";
import styles from "../../maintenancerequest.module.css";

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

// Fallback color palette in case the API returns a category we don't recognize yet
const CATEGORY_COLORS = {
    Electrical: "#5c35c9",
    Plumbing: "#f6613a",
    Appliances: "#8b6fd6",
    "Smart Devices": "#f9a825",
    Other: "#c8c2e0",
};
const FALLBACK_COLORS = ["#5c35c9", "#f6613a", "#8b6fd6", "#f9a825", "#c8c2e0", "#0176b8", "#5ac55e"];

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const YearSelectIcon = (props) => <ChevronDown size={13} {...props} />;

// Fills in every day of the year (up to today for the current year) so the area
// chart has a continuous line, using whatever completed counts the API returned.
const buildDailySeries = (year, apiRows) => {
    const byDate = {};
    (apiRows || []).forEach((row) => {
        byDate[row.date] = Number(row.completed) || 0;
    });

    const series = [];
    const start = new Date(year, 0, 1);
    const now = new Date();
    const end = year === now.getFullYear() ? now : new Date(year, 11, 31);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const isFirstOfMonth = d.getDate() === 1;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        series.push({
            label: isFirstOfMonth ? MONTH_LABELS[d.getMonth()] : "",
            date: key,
            completed: byDate[key] || 0,
        });
    }
    return series;
};

const MaintenanceAnalytics = ({ getAccessTokenSilently }) => {
    const [analyticsYear, setAnalyticsYear] = useState(CURRENT_YEAR);
    const [loading, setLoading] = useState(true);
    const [requestTypes, setRequestTypes] = useState([]);
    const [avgCompletionHours, setAvgCompletionHours] = useState([]);
    const [dailySeries, setDailySeries] = useState([]);

    useEffect(() => {
        let isCancelled = false;

        const fetchAnalytics = async () => {
            setLoading(true);
            try {
                const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
                const response = await axios.get(
                    process.env.REACT_APP_CASE_API_URL + "v1/maintenance-requests/analytics",
                    {
                        params: { year: analyticsYear },
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                if (isCancelled) return;

                const data = response.data.data || {};

                const typesWithColor = (data.requestTypes || []).map((t, idx) => ({
                    label: t.label,
                    count: Number(t.count) || 0,
                    color: CATEGORY_COLORS[t.label] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length],
                }));

                const completionRounded = (data.avgCompletion || []).map((t) => ({
                    label: t.label,
                    hours: Math.round((Number(t.hours) || 0) * 10) / 10,
                }));

                setRequestTypes(typesWithColor);
                setAvgCompletionHours(completionRounded);
                setDailySeries(buildDailySeries(analyticsYear, data.dailyCompleted));
            } catch (error) {
                if (typeof error.response !== "undefined") toast.error(error.response.data.message);
                setRequestTypes([]);
                setAvgCompletionHours([]);
                setDailySeries(buildDailySeries(analyticsYear, []));
            } finally {
                if (!isCancelled) setLoading(false);
            }
        };

        fetchAnalytics();

        return () => {
            isCancelled = true;
        };
    }, [analyticsYear, getAccessTokenSilently]);

    const handleAnalyticsYearChange = (e) => {
        setAnalyticsYear(Number(e.target.value));
    };

    return (
        <React.Fragment>
            <div className={`${styles.analyticsHeader}`}>
                <div className={`${styles.analyticsHeaderTitle}`}>Maintenance Request</div>
                <div className={`${styles.analyticsYearPicker}`}>
                    <span className={`${styles.analyticsYearLabel}`}>Year</span>
                    <Select
                        value={analyticsYear}
                        onChange={handleAnalyticsYearChange}
                        variant="standard"
                        disableUnderline
                        IconComponent={YearSelectIcon}
                        className={`${styles.analyticsYearSelect}`}
                        MenuProps={{ className: styles.yearSelectMenu }}
                        inputProps={{ "aria-label": "Select analytics year" }}
                    >
                        {YEAR_OPTIONS.map((y) => (
                            <MenuItem key={y} value={y} className={styles.yearSelectMenuItem}>
                                {y}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
            </div>

            <div className={`${styles.analyticsRow}`}>
                <RequestsByTypeChart data={requestTypes} year={analyticsYear} loading={loading} />
                <AvgCompletionChart data={avgCompletionHours} loading={loading} />
                <CompletedRequestsChart
                    data={dailySeries}
                    year={analyticsYear}
                    isCurrentYear={analyticsYear === CURRENT_YEAR}
                    loading={loading}
                />
            </div>
        </React.Fragment>
    );
};

export default MaintenanceAnalytics;